import Vue from 'vue';
import axios from 'axios';

// --------------------------------
// Embut dels dossiers i experiments A/B dels anuncis
// Cada visitant té un id anònim i una variant fixa per experiment.
// Els visitants nous reben les variants segons el repartiment del servidor, que cada 3 dies
// dona més trànsit a les que venen més (server/routes/shop/experiments.js).
// La llista del servidor mana; aquesta només és la de reserva. La primera variant és el control.
// --------------------------------
export const EXPERIMENTS = {
  popup_timing: ['5min', '2min', '1min'],
  cta_text: ['veure_mes', 'dossier_assignatura'],
  solution_ad_creative: ['cart', 'community', 'cart_photos'],
};

const AD_TOUCH_TTL = 1000 * 60 * 60 * 24 * 7;
const memory = {};

function read(key) {
  try {
    const value = localStorage.getItem(key);
    if (value !== null) return value;
  } catch (e) {
    // localStorage bloquejat
  }
  return key in memory ? memory[key] : null;
}

function write(key, value) {
  memory[key] = value;
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // localStorage bloquejat
  }
}

function parse(value, fallback) {
  try {
    const parsed = JSON.parse(value);
    return parsed === null ? fallback : parsed;
  } catch (e) {
    return fallback;
  }
}

function uuid() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

const today = () => new Date().toISOString().slice(0, 10);

const visitorId = read('funnel_visitor_id') || uuid();
write('funnel_visitor_id', visitorId);

if (read('funnel_referrer') === null) {
  let referrer = '';
  try {
    const host = new URL(document.referrer).hostname;
    if (host !== window.location.hostname) referrer = host;
  } catch (e) {
    // sense referrer
  }
  write('funnel_referrer', referrer);
}


// --------------------------------
// Assignació de variants
// --------------------------------
const state = Vue.observable({variants: {}});
const stored = parse(read('funnel_variants'), {});

function pick(options, weights = {}) {
  const values = options.map(option => Math.max(0, Number(weights[option]) || 0));
  const total = values.reduce((sum, value) => sum + value, 0);
  if (!total) return options[Math.floor(Math.random() * options.length)];

  let r = Math.random() * total;
  for (let i = 0; i < options.length; i++) {
    r -= values[i];
    if (r < 0) return options[i];
  }
  return options[options.length - 1];
}

let resolveReady;
const ready = new Promise(resolve => {
  resolveReady = resolve;
});

let assigned = false;

function assign(experiments, weights) {
  if (assigned) return;
  assigned = true;

  const variants = {};
  Object.entries(experiments).forEach(([name, options]) => {
    variants[name] = options.includes(stored[name]) ? stored[name] : pick(options, weights[name]);
  });

  state.variants = variants;
  write('funnel_variants', JSON.stringify(variants));
  resolveReady();
}

const needsWeights = Object.entries(EXPERIMENTS).some(([name, options]) => !options.includes(stored[name]));

if (!needsWeights) {
  assign(EXPERIMENTS, {});
} else {
  // Després que main.ts configuri la baseURL d'axios
  setTimeout(() => {
    axios.get('/track/weights', {timeout: 3000})
      .then(({data}) => assign(data.experiments || EXPERIMENTS, data.weights || {}))
      .catch(() => assign(EXPERIMENTS, {}));
  }, 0);
  setTimeout(() => assign(EXPERIMENTS, {}), 4000);
}


// --------------------------------
// Google Analytics 4
// Els mateixos esdeveniments van també a GA amb els noms estàndard d'ecommerce, perquè els
// informes de compra i de checkout de GA funcionin. gtag fa cua fins que es carrega el script.
// --------------------------------
const GA_EVENTS = {
  ad_click: 'select_promotion',
  shop_view: 'view_item_list',
  book_view: 'view_item',
  add_to_cart: 'add_to_cart',
  checkout_view: 'begin_checkout',
  payment_step: 'add_shipping_info',
  pay_click: 'add_payment_info',
  purchase_return: 'purchase',
  shipping_invalid: 'shipping_invalid',
  payment_error: 'payment_error',
  payment_back: 'payment_back',
  checkout_exit: 'checkout_exit',
};

function gaFlat(meta) {
  const params = {};
  Object.entries(meta || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    params[key] = Array.isArray(value) ? value.join(',') : typeof value === 'object' ? JSON.stringify(value) : value;
  });
  return params;
}

function ga(event, {placement, book_id, value, items, transaction_id, meta}) {
  let name = GA_EVENTS[event];
  if (!name || typeof window.gtag !== 'function') return;
  // Stripe torna també quan el pagament ha fallat: a GA només és compra si ha anat bé
  if (event === 'purchase_return' && meta && meta.redirect_status !== 'succeeded') name = 'purchase_failed';

  const params = {...gaFlat(meta), currency: 'EUR'};
  if (placement) {
    params.placement = placement;
    params.promotion_name = placement;
  }
  if (value !== null && value !== undefined) params.value = value / 100;
  if (items) params.items = items;
  else if (book_id) params.items = [{item_id: String(book_id), item_list_name: placement || undefined}];
  if (transaction_id) params.transaction_id = transaction_id;

  window.gtag('event', name, params);
}

ready.then(() => {
  if (typeof window.gtag !== 'function') return;
  // Per segmentar a GA per variant dels experiments
  const props = {funnel_visitor: visitorId};
  Object.entries(state.variants).forEach(([name, variant]) => {
    props[`exp_${name}`] = variant;
  });
  window.gtag('set', 'user_properties', props);
});


// --------------------------------
// Enviament d'esdeveniments
// --------------------------------
function lastAdTouch() {
  const touch = parse(read('funnel_ad_touch'), null);
  return touch && Date.now() - touch.at < AD_TOUCH_TTL ? touch.placement : null;
}

function send(event, {beacon = false} = {}) {
  // En marxar de la pàgina, axios no arriba: sendBeacon sí. Text pla per no fer preflight CORS.
  if (beacon && navigator.sendBeacon) {
    const blob = new Blob([JSON.stringify({visitor_id: visitorId, events: [event]})], {type: 'text/plain'});
    if (navigator.sendBeacon(`${axios.defaults.baseURL || ''}/track`, blob)) return;
  }

  ready.then(() => {
    const payload = {visitor_id: visitorId, events: [event]};

    // Les dades del visitant només s'envien un cop al dia
    if (read('funnel_synced') !== today()) {
      payload.visitor = {
        variants: state.variants,
        traffic_source: {referrer: read('funnel_referrer') || null, ...parse(read('meta_traffic_source'), {})},
        device: window.innerWidth < 960 ? 'mobile' : 'desktop',
      };
      write('funnel_synced', today());
    }

    axios.post('/track', payload).catch(() => {});
  });
}

const funnel = {
  visitorId,
  ready,
  variant(name) {
    return state.variants[name];
  },
  // items i transaction_id només van a GA
  track(event, {placement = null, subject_id = null, book_id = null, value = null, meta = null, items = null, transaction_id = null} = {}, {beacon = false} = {}) {
    const adSource = lastAdTouch();
    const fullMeta = adSource ? {...meta, ad_source: adSource} : meta;
    send({
      event,
      placement,
      subject_id,
      book_id,
      value,
      path: window.location.pathname,
      meta: fullMeta,
    }, {beacon});
    try {
      ga(event, {placement, book_id, value, items, transaction_id, meta: fullMeta});
    } catch (e) {
      // GA no ha de trencar mai la botiga
    }
  },
  // Impressions: n'hi ha prou amb saber que el visitant l'ha vist, no quantes vegades
  trackOncePerDay(event, props = {}) {
    const key = `funnel_once_${event}_${props.placement || ''}`;
    if (read(key) === today()) return;
    write(key, today());
    funnel.track(event, props);
  },
  adClick(placement, props = {}) {
    write('funnel_ad_touch', JSON.stringify({placement, at: Date.now()}));
    funnel.track('ad_click', {...props, placement});
  },
};

Vue.prototype.$funnel = funnel;

export default funnel;
